import { LitElement, html, css } from 'lit';
import { eplibMixin } from './eplib-mixin';
import './eplib-paginate.js';
import EplibAnimationUtils from './eplib-animation-utils.js';
import { EXIST_BASE } from './eplib-constants.js';
import { BootstrapCSS } from '../resources/css/bootstrap.css';
import { EplibStyles } from '../resources/css/style.css.js';

/**
 * Navigates through the list of available texts.
 *
 * @fires eplib-results-received
 * @fires eplib-browse-count-updated
 *
 * @listens eplib-load
 * @listens eplib-paginator-ready
 */

export class EplibBrowseWorks extends eplibMixin(LitElement) {
  static get properties() {
    return {
      ...super.properties,
      works: { type: Array },
      total: { type: Number },
      start: { type: Number },
      perPage: { type: Number, attribute: 'per-page' },
      direction: { type: String },
      loading: { type: Boolean },
      expandedWorks: { type: Object } // Set tracking detail sections
    };
  }

  constructor() {
    super();
    this.works = [];
    this.total = 0;
    this.start = 1;
    this.perPage = 20;
    this.direction = 'nav-next';
    this.loading = true;
    this.expandedWorks = new Set();
  }

  connectedCallback() {
    super.connectedCallback();

    // Subscribe to pagination load events on the "browse" channel
    this.subscribeTo('eplib-load', (event) => {
      this.start = Number(event.detail.params.start);
      this.direction = event.detail.params.direction;
      this.load();
    }, ['browse']);

    // Trigger initial load
    this.subscribeTo('eplib-paginator-ready', (event) => {
      this.load();
    }, ['browse']);
  }

  async load() {
    const easeOut = EplibAnimationUtils._animatePageNav(this.direction, 'out', this.shadowRoot, '.documents', '.loading');

    this.requestUpdate();

    // Read active search filters directly from URL query parameters (or fallback to session storage)
    const filters = {};
    const inputControls = [
      'author', 'title', 'date', 'date2', 'identifier', 'keyword',
      'curator', 'proofreader', 'pagecount', 'pagecount2', 'wordcount', 'wordcount2',
      'genre', 'grade', 'gradeop', 'haspageimages', 'corpus', 'sortField', 'sortDirection'
    ];

    for (const key of inputControls) {
      const val = this.getParameter(key, sessionStorage.getItem(key) || '');
      if (val) filters[key] = val;
    }

    const queryParams = new URLSearchParams({
      start: this.start,
      'per-page': this.perPage,
      ...filters
    });

    try {
      const url = `${EXIST_BASE}/modules/ajax-browse.xql?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'same-origin'
      });
      const data = await response.json();
      
      this.works = data.works || [];
      this.total = data.total || 0;
      if (easeOut) easeOut.complete();
      EplibAnimationUtils._animatePageNav(this.direction, 'in', this.shadowRoot, '.documents', '.loading');

      // Emit pagination events on the "browse" channel
      this.emitTo('eplib-results-received', {
        count: this.total,
        start: this.start,
        data: data
      }, ['browse']);

      // Notify the Light DOM accordion header of the new count
      this.dispatchEvent(new CustomEvent('eplib-browse-count-updated', {
        detail: { count: this.total },
        bubbles: true,
        composed: true
      }));

    } catch (error) {
      console.error("Error fetching works list:", error);
      if (easeOut) easeOut.complete();
      EplibAnimationUtils._stopLoadingAnimation(this.shadowRoot, '.loading');

    }
  }

  _toggleDetails(docid) {
    const nextExpanded = new Set(this.expandedWorks);
    if (nextExpanded.has(docid)) {
      nextExpanded.delete(docid);
    } else {
      nextExpanded.add(docid);
    }
    this.expandedWorks = nextExpanded;
  }

  _downloadLink(work, type) {
    const file = work.docid;
    let extension = '';
    switch (type) {
      case 'stripped':
        extension = '_unadorned.xml.zip';
        break;
      case 'tei':
        extension = '.xml.zip';
        break;
      default:
        extension = '.' + type;
        break;
    }
    return `/downloads/${file.substring(0, 3)}/${file}${extension}`;
  }

  render() {

    return html`
      <div class="text-center empty-message ${this.loading || this.works.length !== 0 ? ' visually-hidden' : ''}" role="alert">
        There are no documents matching the current filter criteria.
      </div>

      <eplib-paginate
        part="paginator"
        per-page="${this.perPage}"
        list-description="documents"
        total-description=""
        range="5"
        total="${this.total}"
        channel="browse">
      </eplib-paginate>

      <div class="loading ${!this.loading ? ' visually-hidden' : ''}">
        <span></span>
      </div>

      <ul class="documents list-unstyled">
        ${this.works.map(work => html`
          <li class="work-item">
            <div class="work-header d-flex justify-content-between align-items-center">
              <div class="mb-1">
                <a href="../works/${work.id}" class="work-title-link">${work.title.length > 120 ? work.title.substring(0, 120) : work.title}</a><span class="title_tooltip">${work.title.length > 120 ? '...' : '.'}
                  <span class="title_text mt-2 bg-light rounded">${work.title.length > 120 ? work.title: ''}</span>
                </span>
                <span></span>
                ${work.author ? html`<span class="work-author">By ${work.author}.</span>` : ''}
                <span></span>
                <span class="work-date">${work.date}.</span>

                ${work.hasImages !== 'no' ? html`
                  <span class="material-icons icon-camera" title="Has page images">photo_camera</span>
                ` : ''}

                <a href="https://earlyprint.org/lab/tool_discovery_engine.html?which_to_do=find_texts&amp;eebo_tcp_id=${work.docid}&amp;n_results=35" 
                   target="_blank" 
                   class="material-icons icon-link"
                   title="Find similar texts">launch</a>

                <button
                  class="btn btn-link btn-sm nav-toggle p-0 text-decoration-none"
                  @click="${() => this._toggleDetails(work.docid)}"
                  title="Show more information">
                  <span class="material-icons">${this.expandedWorks.has(work.docid) ? 'unfold_less' : 'unfold_more'}</span>
                </button>
              </div>
            </div>

            <!-- Expandable Details Section -->
            <div class="work-details mt-2 p-3 bg-light rounded ${this.expandedWorks.has(work.docid) ? 'd-block' : 'd-none'}">
              <p><strong>Pages:</strong> ${work.pages.toLocaleString()} &nbsp;|&nbsp; <strong>Words:</strong> ${work.words.toLocaleString()}</p>
              ${work.subgenre ? html`<p><strong>Genre:</strong> ${work.subgenre}</p>` : ''}
              <p><strong>Page images available:</strong> ${work.hasImages}</p>
              <p>${work.editionStatement ? html`${work.editionStatement}` : ''}</p>
              <p class="mb-2 text-muted">
                ${work.curator ? html`<span>Many corrections by ${work.curator}.</span><br/>` : ''}
                ${work.proofreader ? html`<span>Proofread against page images by ${work.proofreader}.</span>` : html`<span>Unless otherwise noted explicitly, this text has not been fully proofread.</span>`}
              </p>

              ${work.defects.defectRate > 0 ? html`
                <p>Remaining known defects include:</p>
                <ul class="defects-list mb-2 small">
                  ${work.defects.untranscribedForeign > 0 ? html`<li>${work.defects.untranscribedForeign} untranscribed word(s) in a non-Roman alphabet.</li>` : ''}
                  ${work.defects.untranscribedMath > 0 ? html`<li>${work.defects.untranscribedMath} mathematical symbol(s).</li>` : ''}
                  ${work.defects.untranscribedMusic > 0 ? html`<li>${work.defects.untranscribedMusic} passage(s) of musical notation.</li>` : ''}
                  ${work.defects.defectiveToken > 0 ? html`<li>${work.defects.defectiveToken} missing or incompletely transcribed token(s).</li>` : ''}
                  ${work.defects.missingChunk > 0 ? html`<li>${work.defects.missingChunk} missing passage(s).</li>` : ''}
                  ${work.defects.missingPages > 0 ? html`<li>${work.defects.missingPages} missing page(s).</li>` : ''}
                </ul>
                ${work.defects.defectRangePerGrade ? html`<p class="small">${work.defects.defectRangePerGrade}</p>` : ''}
              ` : ''}
            </div>
          </li>
          <li>
            <!-- Toolbar buttons for downloads -->
            <div class="btn-group toolbar mt-2">
              <a class="btn btn-outline-secondary btn-sm" href="${this._downloadLink(work, 'pdf')}" download title="Download as PDF">PDF</a>
              <a class="btn btn-outline-secondary btn-sm" href="${this._downloadLink(work, 'epub')}" download title="Download as EPUB ebook">ePub</a>
              <a class="btn btn-outline-secondary btn-sm" href="${this._downloadLink(work, 'tei')}" download title="Download TEI XML">XML</a>
            </div>
          </li>
        `)}
      </ul>
    `;
  }

  static get styles() {
    return [
    BootstrapCSS,
    EplibStyles,
    css`
      .documents {
        font-family: Georgia, "Times New Roman", serif; !important;
        display: inline-block;
        .work-header {
            font-size: 16px;
            margin-top: 10px;
            margin-bottom: 10px;
        }
        .toolbar {
            float: right;
            margin-top: 0;
        }
        >li {
            list-style: none;
            border-top: 1px solid #C0C0C0;
            font-size: 16px;
        }
        >li:nth-child(odd) {
            width: 75%;
            float: left;
        }
        >li:nth-child(even) {
            width: 25%;
            float: left;
        }
        .clear {
            clear: both;
        }
        .btn {
            padding: 5px 6px;
        }
      }
      .icon-camera, .icon-link {
        font-size: 1.1rem;
        color: #777;
        vertical-align: middle;
      }
      .nav-toggle {
        color: #555;
        vertical-align: middle;
      }
      .nav-toggle:hover {
        color: #000;
        cursor: help;
      }
      .work-details {
        font-size: 0.75rem;
      }
      .defects-list {
        padding-left: 20px;
        border-left: 3px solid #8c2a38;
        color: #8c2a38;
      }
      .toolbar a {
        font-size: 0.8rem;
        margin-right: 5px;
        border-color: #ddd;
        color: #666;
      }
      .toolbar a:hover {
        background-color: #f5f5f5;
        color: #333;
      }
      .work-title-link {
        margin-right: 0;
      }

      .title_tooltip {
        position: relative;
        display: inline-block;
        margin-right: 0;
      }

      .title_tooltip .title_text {
        visibility: hidden;
        position: absolute;
        z-index: 500 !important;
        left: 50%;
        top: 0.75rem;
        font-size: 0.75rem;
        padding: 0.1rem;
        width: 24rem;
        margin-left: -4rem;
      }

      .title_tooltip:hover .title_text {
        visibility: visible;
      }
    `];
  }
}

customElements.define('eplib-browse-works', EplibBrowseWorks);
